'use client';

import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons';
import * as React from 'react';

import { Button } from '../components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover';
import { ScrollArea } from '../components/scrollArea';
import { cn } from '../utils';

export function MultiCombobox({
  value: selectedValues,
  values,
  onChange,
  disabled = false,
  emptyLabel = null,
  emptySearch = '',
  placeholder,
  id,
  triggerClassName,
  contentClassName,
  side,
  isLoading = false,
  renderSelectedLabel,
}: {
  values: Array<{
    label: string;
    displayLabel?: React.ReactNode;
    value: string;
  }>;
  onChange: (newValues: string[]) => void;
  value: string[];
  emptySearch?: string;
  disabled?: boolean;
  id?: string;
  emptyLabel?: React.ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  isLoading?: boolean;
  /** Override the default "N selected" text when 2+ items are selected. Receives the labels of selected items. */
  renderSelectedLabel?: (selectedLabels: string[]) => React.ReactNode;
} & Pick<React.ComponentProps<typeof CommandInput>, 'placeholder'> &
  Pick<React.ComponentProps<typeof PopoverContent>, 'side'>) {
  const [open, setOpen] = React.useState(false);

  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  const triggerLabel = React.useMemo(() => {
    if (selectedValues.length === 0) return emptyLabel;
    if (selectedValues.length === 1) {
      const item = values.find(({ value }) => value === selectedValues[0]);
      return item?.displayLabel || item?.label || selectedValues[0];
    }
    if (renderSelectedLabel) {
      const selectedLabels = selectedValues.map((v) => {
        const item = values.find(({ value }) => value === v);
        return item?.label ?? v;
      });
      return renderSelectedLabel(selectedLabels);
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, values, emptyLabel, renderSelectedLabel]);

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === ' ') {
      event.preventDefault();
      setOpen(!open);
    }
  };

  const allSelected = values.length > 0 && selectedValues.length === values.length;

  const handleSelect = (itemValue: string) => {
    if (selectedSet.has(itemValue)) {
      onChange(selectedValues.filter((v) => v !== itemValue));
    } else {
      onChange([...selectedValues, itemValue]);
    }
  };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          tabIndex={0}
          variant="outline"
          role="combobox"
          disabled={disabled || isLoading}
          isLoading={isLoading}
          aria-expanded={open}
          onKeyDown={handleTriggerKeyDown}
          className={cn(triggerClassName, 'h-min w-full justify-between ')}
        >
          <span className={cn(selectedValues.length === 0 && 'font-normal opacity-60', 'truncate')}>
            {triggerLabel}
          </span>
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        className={cn('w-56 p-0', contentClassName)}
        sideOffset={5}
        avoidCollisions={true}
        collisionPadding={8}
      >
        <Command
          className="bg-white"
          filter={(value, search) => {
            return values
              .find(({ value: v }) => value === v)
              ?.label.toLowerCase()
              .includes(search.toLowerCase())
              ? 1
              : 0;
          }}
        >
          <CommandInput placeholder={placeholder} className="h-9 border-none bg-white" />
          {emptySearch && <CommandEmpty className="bg-white">{emptySearch}</CommandEmpty>}
          {values.length > 0 && (
            <div className="flex justify-end gap-2 border-b border-gray-100 px-2 py-1">
              {!allSelected && (
                <button
                  type="button"
                  className="text-xs text-gray-500 hover:text-gray-800"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onChange(values.map((v) => v.value))}
                >
                  Select All
                </button>
              )}
              {selectedValues.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-gray-500 hover:text-gray-800"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onChange([])}
                >
                  Clear
                </button>
              )}
            </div>
          )}
          <CommandList className="bg-white">
            <ScrollArea
              type="always"
              className={cn(
                'bg-white [&>div>div]:!block',
                values.length <= 8 ? 'max-h-fit' : 'h-[300px]',
              )}
            >
              <CommandGroup className="bg-white">
                {values.map(({ value, label, displayLabel }) => (
                  <CommandItem
                    key={value}
                    value={value}
                    className="bg-white"
                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === 'Enter') {
                        handleSelect(value);
                      }
                    }}
                    onSelect={handleSelect}
                  >
                    {displayLabel || label}
                    <CheckIcon
                      className={cn(
                        'ml-auto h-4 w-4',
                        selectedSet.has(value) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
